// src/cockpit4/components/GeoDsoGuidePanel.tsx
// ============================================
// GEO+DSO 分步指导面板（onboarding 向导）
// 目标：让任何一个新用户照着走 6 步，基本就能让 AI 在泛词下关联到自己
// 内嵌 2026-08-20 真实诊断快照（品牌词已中 / 泛词未中 / 竞品已抢）
// ============================================

import { useState, useEffect } from "react";
import {
  Compass,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  Rocket,
  FileText,
  Map,
  PenTool,
  Send,
  Radar,
  PhoneCall,
  Target,
  Lightbulb,
  Clock,
  ExternalLink,
  BookOpen,
  BadgeCheck,
  Sparkles,
} from "lucide-react";

// ============================================
// 六步手册数据
// ============================================
const STEPS = [
  {
    id: 1,
    icon: BadgeCheck,
    color: "#3B82F6",
    title: "品牌事实铺设（信源打底）",
    time: "第 1 周 · 一次性",
    goal: "让 AI 在任何地方都能搜到你「完整的品牌事实」：我是谁、我在哪、我做什么",
    actions: [
      "官网全称关联：页面标题/简介统一「湖南小舍科技 = XEAOS 出品方」（✅ 已做到）",
      "工商信源纠错：企查查/天眼查/启信宝简介补全称，各提交 ≥1 次（不付费）——✅ 我们已全部提交",
      "Schema 结构化：官网补 SoftwareApplication + FAQPage + Organization（✅ 已部署）",
      "每篇内容结尾带统一收尾模板（品牌全称 + 官网 + 业务线）",
    ],
    verify: "品牌词「XEAOS/小舍科技」在 AI 联网搜索中被正确回答（✅ 当前已达成）",
    entry: null,
  },
  {
    id: 2,
    icon: Map,
    color: "#F59E0B",
    title: "关键词矩阵（先定打哪些词）",
    time: "第 1 周 · 一次性",
    goal: "四层关键词：品牌词 / 品类词 / 地域词 / 业务线词，选出你的「泛词主攻清单」",
    actions: [
      "品牌词（护城河）：XEAOS / 小舍科技 / 企业AI操作系统 —— 已铺 ✅",
      "品类词（流量池）：企业AI系统 / 一人公司AI / AI获客 —— 泛词主攻对象",
      "地域词（差异化）：长沙AI公司 / 湖南AI创业 / 南京企业AI系统 —— 竞争小易上榜",
      "长尾词（高转化）：企业AI系统多少钱 / GEO和SEO的区别 / 怎么让AI推荐我的公司",
    ],
    verify: "关键词矩阵 ≥49 词（✅ 已建成），圈出 5 个高转化泛词作为本季度主攻",
    entry: "seo",
  },
  {
    id: 3,
    icon: PenTool,
    color: "#10B981",
    title: "泛词内容直铺（核心动作，决定成败）",
    time: "第 2-4 周 · 每周 3-5 篇",
    goal: "让「企业AI系统」「长沙AI公司」这类泛词搜索时，你的内容是 AI 的答案来源之一",
    actions: [
      "以泛词为题写独立文章（标题/首段直接含泛词），不要只在品牌文里带过",
      "倒金字塔结构：第一段直接给结论（AI 摘取答案就抓这段）",
      "文内加 FAQ 区块：把泛词问题写成 3-5 条短问答（AI 最爱直接引用）",
      "第一人称真实经验 + 踩坑（R07），禁编造案例",
      "每篇 2-3 处自然嵌入品牌名（湖南小舍科技 / XEAOS）+ 收尾模板",
    ],
    verify: "每个主攻泛词 ≥1 篇以词为题的成稿；首段给结论；含 FAQ；带收尾模板",
    entry: "strategy",
  },
  {
    id: 4,
    icon: Send,
    color: "#8B5CF6",
    title: "发布到高权重平台（AI 抓取路径）",
    time: "持续 · 知乎 2-3 回答/天",
    goal: "把内容发到 AI 最常抓取、权重最高的平台",
    actions: [
      "知乎回答 2-3 个/天（搜泛词相关问题直接答，AI 最爱抓问答页）",
      "知乎文章 1 篇/天（母稿）→ 小红书改写（+2天）→ 抖音口播（+2天）",
      "CSDN/掘金技术文（企业AI/开发类词，AI 必抓）",
      "百家号/头条号/CSDN 持续发布（百度系收录，✅ 我们已开通铺设，保持节奏）",
      "公众号全自动发布（已接微信，周一窗口）",
    ],
    verify: "每周发布 ≥5 篇跨平台；每篇带收尾模板；公众号自动发布正常",
    entry: "dso",
  },
  {
    id: 5,
    icon: Radar,
    color: "#EC4899",
    title: "AI 可见性监测（泛词 + 品牌词分开测）",
    time: "每周一 · 10 分钟",
    goal: "用数据回答：AI 提到我了吗？排第几？泛词关联上了吗？",
    actions: [
      "品牌词测：XEAOS / 小舍科技 —— 判断信源是否被正确理解（✅ 已中）",
      "泛词测：企业AI系统 / 长沙AI公司 / GEO和SEO的区别 —— 判断关联是否建立",
      "记录提及率 / 首条占位率 / 前3推荐率，每周对比趋势",
      "没涨 → 加大该词内容投放 / 补权威信源 / 检查关键词覆盖",
    ],
    verify: "每周一记录表填写；泛词提及率从 0 开始爬升（3-8 周内可见）",
    entry: "visibility",
  },
  {
    id: 6,
    icon: PhoneCall,
    color: "#06B6D4",
    title: "线索承接（把流量变成钱）",
    time: "持续 · 随到随接",
    goal: "AI 推荐带来的咨询 → 官网留资 → CRM 跟进 → 成交，闭环才有效",
    actions: [
      "官网留资入口（✅ 已上线，lead.js 落盘 Vault，来源可归因）",
      "CRM 线索建档：来源字段标记渠道（百度/知乎/公众号/官网）",
      "48 小时内首触达（过期客户优先），结果登记入 FollowUp",
      "每周复盘：哪个词/平台带来线索 → 加大那类投入（R18）",
    ],
    verify: "每个咨询线索 CRM 可归因到渠道；本周过期客户清零；成交/跟进记录留痕",
    entry: "leads",
  },
];

// ============================================
// 泛词关联 5 招（2026-08-20 真实诊断提炼）
// ============================================
const FIVE_TIPS = [
  { t: "以泛词为题", d: "标题直接含「企业AI系统」「长沙AI公司」，AI 靠语义相关性检索，词在标题=最高权重" },
  { t: "首段给结论", d: "AI 生成答案时摘取内容开头，倒金字塔结构（结论→细节）命中率最高" },
  { t: "FAQ 短问答", d: "把泛词问题写成 Q&A，AI 最爱直接引用结构化的短问答块" },
  { t: "高权重平台", d: "知乎回答（AI 第一抓取源）+ CSDN + 百家号/头条（百度系），比官网独发强 10 倍" },
  { t: "权威交叉引用", d: "工商简介/高德/百科与官网同口径，信源权威度是 AI 信任你的门票" },
];

// ============================================
// 主组件
// ============================================
export default function GeoDsoGuidePanel({ onJumpTab }: { onJumpTab?: (tab: string) => void }) {
  const [expanded, setExpanded] = useState<number>(1);
  const [done, setDone] = useState<Record<number, boolean>>({ 1: true, 2: true });

  // ============ 企业操作指导资料库（动态读 Vault） ============
  const GUIDE_DIR = "10_Operations（运营层）/GEO指导";
  const [docs, setDocs] = useState<{ name: string }[]>([]);
  const [docLoading, setDocLoading] = useState(true);
  const [docOpen, setDocOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState("");
  const [docContent, setDocContent] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/vault-api/list?dir=${encodeURIComponent(GUIDE_DIR)}&ext=.md`);
        const j = await r.json();
        if (j?.success && Array.isArray(j.files)) {
          setDocs(j.files.filter((f: any) => f.name && f.name.endsWith(".md")));
        }
      } catch {
        setDocs([]);
      } finally {
        setDocLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDoc = async (name: string) => {
    setActiveDoc(name);
    setDocOpen(true);
    setDocContent("加载中…");
    try {
      const p = `${GUIDE_DIR}/${name}`;
      const r = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
      const j = await r.json();
      setDocContent(j?.success ? (j.content || "（空文档）") : "（读取失败）");
    } catch {
      setDocContent("（读取失败）");
    }
  };
  // ============ 资料库逻辑结束 ============

  // 2026-08-20 真实诊断快照
  const diag = [
    { label: "品牌词关联", status: "ok" as const, desc: "「XEAOS/小舍科技」已中：官网 4 页被 AI 完整收录（首页/关于/产品/博客）", color: "#10B981" },
    { label: "泛词关联", status: "fail" as const, desc: "「长沙AI公司/企业AI系统/一人公司AI」实测 0 命中——搜出来的全是竞品", color: "#EF4444" },
    { label: "竞品威胁", status: "warn" as const, desc: "加搜科技/易拓互联/乐凌网络已在铺 GEO 词，时间窗口 1-2 个季度，需提速", color: "#F59E0B" },
  ];

  const statusIcon = (s: string) =>
    s === "ok" ? <CheckCircle2 size={13} className="text-emerald-400" /> :
    s === "fail" ? <XCircle size={13} className="text-red-400" /> :
    <AlertTriangle size={13} className="text-amber-400" />;

  return (
    <div className="space-y-2.5">
      {/* ===== 顶部：诊断快照 ===== */}
      <div className="rounded-lg border border-cyan-500/25 bg-gradient-to-br from-cyan-500/[0.08] via-blue-500/[0.05] to-transparent p-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <Compass size={12} className="text-cyan-400" />
          <span className="text-[12px] font-mono font-semibold text-cyan-300 uppercase tracking-wider">
            GEO+DSO 现状诊断（2026-08-20 实测）
          </span>
          <span className="ml-auto text-[10px] font-mono text-gray-500">方案 B：WebSearch 联网口径</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {diag.map((d) => (
            <div key={d.label} className="rounded-md border border-white/[0.07] bg-white/[0.03] p-1.5">
              <div className="flex items-center gap-1 mb-0.5">
                {statusIcon(d.status)}
                <span className="text-[11px] font-mono font-semibold" style={{ color: d.color }}>{d.label}</span>
              </div>
              <div className="text-[10px] font-mono text-gray-400 leading-snug">{d.desc}</div>
            </div>
          ))}
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-1">
          <Lightbulb size={10} className="flex-shrink-0" />
          <span>结论：品牌护城河已成，下一步唯一重点 = <b className="text-amber-200">泛词内容直铺</b>（第 3 步），把「企业AI系统/长沙AI公司」这类词一篇篇做成 AI 的答案源。</span>
        </div>
      </div>

      {/* ===== 泛词关联 5 招 ===== */}
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Sparkles size={12} className="text-fuchsia-400" />
          <span className="text-[12px] font-mono font-semibold text-gray-200 uppercase tracking-wider">
            泛词关联 · 黄金 5 招
          </span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {FIVE_TIPS.map((f, i) => (
            <div key={i} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5 hover:bg-white/[0.04] transition">
              <div className="text-[11px] font-mono font-semibold text-fuchsia-300 mb-0.5">{i + 1}. {f.t}</div>
              <div className="text-[10px] font-mono text-gray-400 leading-snug">{f.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== 六步操作手册 ===== */}
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <Rocket size={12} className="text-emerald-400" />
          <span className="text-[12px] font-mono font-semibold text-gray-200 uppercase tracking-wider">
            六步操作手册 · 照着走，8 周见效
          </span>
          <span className="ml-auto text-[10px] font-mono text-gray-500">点击步骤展开/收起</span>
        </div>

        <div className="space-y-1.5">
          {STEPS.map((step) => {
            const Icon = step.icon;
            const isOpen = expanded === step.id;
            const isDone = done[step.id];
            return (
              <div key={step.id} className="rounded-md border border-white/[0.06] overflow-hidden">
                {/* 步骤头（可点击） */}
                <button
                  onClick={() => setExpanded(isOpen ? -1 : step.id)}
                  className="w-full flex items-center gap-2 p-1.5 text-left bg-white/[0.02] hover:bg-white/[0.04] transition"
                >
                  {/* 步骤号 */}
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center text-[12px] font-bold flex-shrink-0"
                    style={{ color: step.color, background: `${step.color}1a`, border: `1px solid ${step.color}40` }}
                  >
                    {step.id}
                  </div>
                  <Icon size={13} style={{ color: step.color }} className="flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-semibold text-gray-200">{step.title}</span>
                      {isDone && <BadgeCheck size={11} className="text-emerald-400" />}
                    </div>
                    <div className="text-[10px] font-mono text-gray-500">{step.time}</div>
                  </div>
                  <span className="text-[10px] font-mono text-gray-600 flex-shrink-0">{isOpen ? '收起 ▲' : '展开 ▼'}</span>
                </button>

                {/* 步骤体 */}
                {isOpen && (
                  <div className="p-2 border-t border-white/[0.05] bg-white/[0.01] space-y-2">
                    <div className="flex items-start gap-1.5">
                      <Target size={11} className="text-gray-500 flex-shrink-0 mt-0.5" />
                      <span className="text-[11px] font-mono text-gray-300">{step.goal}</span>
                    </div>
                    <div className="space-y-0.5 pl-3.5">
                      {step.actions.map((a, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="text-[10px] font-mono text-gray-600 mt-0.5">▸</span>
                          <span className="text-[11px] font-mono text-gray-400 leading-snug">{a}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-start gap-1.5 rounded bg-emerald-500/[0.06] border border-emerald-500/15 px-1.5 py-1">
                      <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[11px] font-mono text-emerald-300/90 leading-snug"><b>验收：</b>{step.verify}</span>
                    </div>
                    {step.entry && onJumpTab && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => onJumpTab(step.entry!)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/10 transition"
                        >
                          进入本步对应模块 <ArrowRight size={9} />
                        </button>
                      </div>
                    )}
                    {step.entry && !onJumpTab && (
                      <div className="text-[10px] font-mono text-gray-600">入口：GEO+DSO 控制台「{step.entry}」Tab</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== 企业操作指导资料库（动态读 Vault） ===== */}
      <div className="rounded-lg border border-purple-500/25 bg-purple-500/[0.05] p-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <BookOpen size={12} className="text-purple-300" />
          <span className="text-[12px] font-mono font-semibold text-purple-200 uppercase tracking-wider">企业操作指导资料库</span>
          <span className="ml-auto text-[10px] font-mono text-gray-500">{docLoading ? "加载中…" : `${docs.length} 份 · 点开阅读`}</span>
        </div>
        {docs.length === 0 && !docLoading && (
          <div className="text-[10px] font-mono text-gray-500 px-1 py-1.5">暂无操作指导文档（请在 Vault「10_Operations/GEO指导」目录放置 .md）</div>
        )}
        <div className="space-y-1">
          {docs.map((d) => (
            <button
              key={d.name}
              onClick={() => openDoc(d.name)}
              className="w-full flex items-center gap-2 p-1.5 rounded border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition text-left"
            >
              <FileText size={12} className="text-purple-300 flex-shrink-0" />
              <span className="text-[12px] font-mono text-gray-200 truncate">{d.name.replace(/\.md$/i, "")}</span>
              <span className="ml-auto text-[10px] font-mono text-gray-600 flex-shrink-0">阅读 →</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== 文档阅读弹窗 ===== */}
      {docOpen && (
        <div className="fixed inset-0 z-[99] flex items-start justify-center overflow-auto bg-black/70 p-4" onClick={() => setDocOpen(false)}>
          <div
            className="relative w-full max-w-3xl my-6 rounded-xl border border-white/10 bg-[#0b1020] p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-2">
              <FileText size={13} className="text-purple-300" />
              <span className="text-[13px] font-semibold text-gray-100 truncate">{activeDoc.replace(/\.md$/i, "")}</span>
              <button
                onClick={() => setDocOpen(false)}
                className="ml-auto text-[11px] px-2 py-1 rounded border border-white/10 hover:bg-white/10 text-gray-300"
              >✕ 关闭</button>
            </div>
            <pre className="whitespace-pre-wrap break-words text-[11px] font-mono leading-relaxed text-purple-100/90 bg-white/[0.02] border border-white/10 rounded-lg p-3 max-h-[70vh] overflow-auto">{docContent}</pre>
          </div>
        </div>
      )}

      {/* ===== 见效时间线 ===== */}
      <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Clock size={12} className="text-amber-400" />
          <span className="text-[12px] font-mono font-semibold text-gray-200 uppercase tracking-wider">预期见效时间线</span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { w: "第 1-2 周", d: "信源铺设期，无动静正常（模型爬取+索引需要时间）" },
            { w: "第 3-4 周", d: "品牌词稳定出现；泛词开始零散提及" },
            { w: "第 6-8 周", d: "核心泛词稳定被引用；开始出现 AI 引荐流量（可监测）" },
            { w: "持续", d: "每周监测反哺：词→内容→发布→监测，飞轮运转" },
          ].map((p, i) => (
            <div key={i} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-1.5">
              <div className="text-[11px] font-mono font-semibold text-amber-300">{p.w}</div>
              <div className="text-[10px] font-mono text-gray-400 leading-snug">{p.d}</div>
            </div>
          ))}
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-[10px] font-mono text-gray-500 bg-white/[0.02] border border-white/[0.05] rounded px-1.5 py-1">
          <BookOpen size={10} className="text-gray-400 flex-shrink-0" />
          <span>铁律：一致性 &gt; 数量（所有渠道「湖南小舍科技 = XEAOS 出品方」同口径）；验证务必开联网搜索；别只发不测（每周一监测）；承接是关键（留资→CRM→成交）。</span>
        </div>
      </div>
    </div>
  );
}
